// "Track my order" and "Report a problem" (issues 35, 42, 43): checks the forms with
// cck-orders-core.js and asks CCK Ops through the Shopify app proxy. When CCK Ops can't be reached,
// the page says how else to reach me, so nobody is left without an answer.
import {
  claimErrors,
  isEmail,
  MAX_PHOTOS,
  orderNumber,
  replacementRule,
  trackingStages,
} from "@cck/orders-core";

/** Shows each field's message under it, marks the field, and focuses the first problem. */
function showErrors(
  /** @type {HTMLFormElement} */ form,
  /** @type {Record<string, string>} */ errors,
) {
  for (const p of form.querySelectorAll("[data-cck-error]")) {
    const key = p.getAttribute("data-cck-error") ?? "";
    p.textContent = errors[key] ?? "";
    const field = form.querySelector(`[name="${key}"]`);
    if (field instanceof HTMLInputElement)
      field.setAttribute("aria-invalid", String(Boolean(errors[key])));
  }
  const first = Object.keys(errors)[0];
  const el = first ? form.querySelector(`[name="${first}"]`) : null;
  if (el instanceof HTMLElement) el.focus();
}

/**
 * Issue 288: our emails' links carry the order's own key (from Shopify's order status link)
 * instead of the customer's email. With one, the email field isn't needed: it's hidden, and the
 * key goes wherever the email would.
 */
let linkKey = new URLSearchParams(location.search).get("key");
function useLinkKey(/** @type {HTMLFormElement} */ form) {
  if (!linkKey) return;
  const email = form.querySelector('[name="email"]');
  if (email instanceof HTMLInputElement) {
    email.required = false;
    const field = email.closest(".cck-calc__field");
    if (field instanceof HTMLElement) field.hidden = true;
  }
}
/**
 * Issue 335: a key that doesn't match (an old or forwarded link) falls back to the email: the
 * field comes back, and the page says why. Returns the message to show.
 */
function dropLinkKey(/** @type {HTMLFormElement} */ form) {
  linkKey = null;
  const email = form.querySelector('[name="email"]');
  if (email instanceof HTMLInputElement) {
    email.required = true;
    const field = email.closest(".cck-calc__field");
    if (field instanceof HTMLElement) field.hidden = false;
    email.focus();
  }
  return "That link didn't find your order. Enter the email you ordered with instead.";
}

// ---- Track my order --------------------------------------------------------------------------

const track = document.querySelector("[data-cck-track]");
const trackForm = track?.querySelector("[data-cck-track-form]");
if (track instanceof HTMLElement && trackForm instanceof HTMLFormElement) {
  trackForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(trackForm);
    const order = orderNumber(String(data.get("order") ?? ""));
    // Issue 288: the email typed, or the link's key.
    const email = String(data.get("email") ?? "").trim() || linkKey || "";
    /** @type {Record<string, string>} */
    const errors = {};
    if (!order) errors.order = "Your order number is in your confirmation email, like #1042.";
    if (!linkKey && !isEmail(email)) errors.email = "The email you ordered with, please.";
    showErrors(trackForm, errors);
    if (Object.keys(errors).length) return;

    const stages = track.querySelector("[data-cck-track-stages]");
    const fallback = track.querySelector("[data-cck-track-fallback]");
    try {
      const url = `${track.dataset.endpoint}?order=${encodeURIComponent(order ?? "")}&email=${encodeURIComponent(email)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const o = await res.json();
      if (o.found === false) {
        if (stages instanceof HTMLElement) stages.hidden = true;
        const link = track.querySelector("[data-cck-track-link]");
        if (link instanceof HTMLElement) link.hidden = true;
        if (linkKey) showErrors(trackForm, { email: dropLinkKey(trackForm) });
        else
          showErrors(trackForm, {
            order:
              "I couldn't find that order with that email. Check both against your confirmation email.",
          });
        return;
      }
      if (!(stages instanceof HTMLElement)) return;
      stages.innerHTML = "";
      // Issue 287: a dispatch date that moved after checkout says so; issue 482: and why, when it
      // was moved by hand (the customer was emailed the same).
      if (o.movedFrom && o.dispatchDate)
        o.dispatchDate = o.movedWhy
          ? `${o.dispatchDate} (moved from ${o.movedFrom}, because ${String(o.movedWhy).replace(/[.\s]+$/, "")})`
          : `${o.dispatchDate} (moved from ${o.movedFrom}; I'll message you about it)`;
      for (const s of trackingStages(o)) {
        const li = document.createElement("li");
        li.className = `cck-orders__stage cck-orders__stage--${s.state}`;
        li.append(Object.assign(document.createElement("strong"), { textContent: s.label }));
        if (s.note)
          li.append(Object.assign(document.createElement("span"), { textContent: s.note }));
        stages.append(li);
      }
      stages.hidden = false;
      // The courier's own tracking (issue 42): Rapido's live link and OTP, or the docket number.
      const link = track.querySelector("[data-cck-track-link]");
      if (link instanceof HTMLElement) {
        link.textContent = "";
        const t = o.tracking;
        link.hidden = !t;
        if (t) {
          const what =
            t.company === "Rapido"
              ? `Your Rapido rider is on the way.${t.number ? ` Share ${t.number.replace(/^OTP[-:]?\s*/i, "OTP ")} at the door.` : ""}`
              : `${t.company} docket ${t.number ?? ""}: enter it on their tracking page.`;
          link.append(what);
          // Only a web link (issue 238): the tracking link comes from the fulfilment, which an app
          // or the admin could set to anything, javascript: included.
          if (t.url && /^https:\/\//i.test(t.url)) {
            link.append(" ");
            link.append(
              Object.assign(document.createElement("a"), {
                href: t.url,
                textContent:
                  t.company === "Rapido" ? "Follow the ride live" : `Open ${t.company} tracking`,
                target: "_blank",
                rel: "noopener",
              }),
            );
          }
        }
      }
      showCancel(o, String(order), email);
      showMissing(o, String(order), email);
      if (fallback instanceof HTMLElement) fallback.hidden = true;
    } catch {
      if (stages instanceof HTMLElement) stages.hidden = true;
      if (fallback instanceof HTMLElement) fallback.hidden = false;
    }
  });

  // Issue 256: cancelling within 6 hours, for a full refund. Two taps: "Cancel my order", then
  // "Yes, cancel it and refund me".
  const cancel = track.querySelector("[data-cck-track-cancel]");
  const text = cancel?.querySelector("[data-cck-cancel-text]");
  const start = cancel?.querySelector("[data-cck-cancel-start]");
  const confirm = cancel?.querySelector("[data-cck-cancel-confirm]");
  const keep = cancel?.querySelector("[data-cck-cancel-keep]");
  /** @type {{ order: string, email: string } | null} */
  let current = null;
  const buttons = (/** @type {"start" | "confirm" | "none"} */ step) => {
    if (start instanceof HTMLElement) start.hidden = step !== "start";
    if (confirm instanceof HTMLElement) confirm.hidden = step !== "confirm";
    if (keep instanceof HTMLElement) keep.hidden = step !== "confirm";
  };
  const say = (/** @type {string} */ s) => {
    if (text) text.textContent = s;
  };
  /** @param {any} o @param {string} order @param {string} email */
  function showCancel(o, order, email) {
    if (!(cancel instanceof HTMLElement)) return;
    current = { order, email };
    if (o.cancelled) {
      cancel.hidden = false;
      say("This order is cancelled.");
      buttons("none");
    } else if (o.cancelUntil) {
      const until = new Date(o.cancelUntil).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
      });
      cancel.hidden = false;
      // Issue 274: the same rule everywhere.
      say(
        `Changed your mind? You can cancel until ${until}, as long as I haven't started cooking it, for a full refund.`,
      );
      buttons("start");
    } else if (o.cancelNote) {
      // Issue 274: why it can't be cancelled now (being cooked, or past the 6 hours).
      cancel.hidden = false;
      say(o.cancelNote);
      buttons("none");
    } else {
      cancel.hidden = true;
    }
  }
  start?.addEventListener("click", () => {
    say(`Cancel order ${current?.order ?? ""}? Your full refund goes back to how you paid.`);
    buttons("confirm");
    if (confirm instanceof HTMLElement) confirm.focus();
  });
  keep?.addEventListener("click", () => {
    say("Your order stays as it is.");
    buttons("start");
  });
  confirm?.addEventListener("click", async () => {
    if (!current || !(confirm instanceof HTMLButtonElement)) return;
    confirm.disabled = true;
    say("Cancelling…");
    try {
      const body = new FormData();
      body.set("order", current.order);
      body.set("email", current.email);
      const res = await fetch(track.dataset.cancelEndpoint ?? "", { method: "POST", body });
      const r = await res.json();
      if (r.cancelled) {
        say(
          "Cancelled. Your full refund is on its way to how you paid (it usually shows in 5–7 working days), and you'll get an email from me about it.",
        );
        buttons("none");
      } else {
        say(r.error ?? "I couldn't find that order with that email.");
        buttons("none");
      }
    } catch {
      say("That didn't go through. Please message me on WhatsApp and I'll cancel it for you.");
      buttons("none");
    } finally {
      confirm.disabled = false;
    }
  });

  // Issue 271: "I haven't received it", once the order is marked delivered. Two taps, like cancel.
  const missing = track.querySelector("[data-cck-track-missing]");
  const mText = missing?.querySelector("[data-cck-missing-text]");
  const mStart = missing?.querySelector("[data-cck-missing-start]");
  const mConfirm = missing?.querySelector("[data-cck-missing-confirm]");
  const mKeep = missing?.querySelector("[data-cck-missing-keep]");
  /** @type {{ order: string, email: string, ncr: boolean } | null} */
  let missingFor = null;
  const mButtons = (/** @type {"start" | "confirm" | "none"} */ step) => {
    if (mStart instanceof HTMLElement) mStart.hidden = step !== "start";
    if (mConfirm instanceof HTMLElement) mConfirm.hidden = step !== "confirm";
    if (mKeep instanceof HTMLElement) mKeep.hidden = step !== "confirm";
  };
  const mSay = (/** @type {string} */ s) => {
    if (mText) mText.textContent = s;
  };
  const whoDelivers = () => (missingFor?.ncr ? "the rider" : "the courier");
  /** @param {any} o @param {string} order @param {string} email */
  function showMissing(o, order, email) {
    if (!(missing instanceof HTMLElement)) return;
    missingFor = { order, email, ncr: o.courier === "Rapido" };
    if (o.notReceivedAt) {
      const when = new Date(o.notReceivedAt).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
      missing.hidden = false;
      mSay(
        `You told me on ${when} that it hasn't arrived. I'm checking with ${whoDelivers()} and I'll be in touch.`,
      );
      mButtons("none");
    } else if (o.canReportMissing) {
      missing.hidden = false;
      mSay(
        "As far as I know, it's been delivered. If it hasn't reached you, tell me and I'll find out what happened.",
      );
      mButtons("start");
      if (location.hash === "#not-received") missing.scrollIntoView({ block: "center" });
    } else {
      missing.hidden = true;
    }
  }
  mStart?.addEventListener("click", () => {
    mSay(
      `Tell me order ${missingFor?.order ?? ""} hasn't arrived? I'll check with ${whoDelivers()}.`,
    );
    mButtons("confirm");
    if (mConfirm instanceof HTMLElement) mConfirm.focus();
  });
  mKeep?.addEventListener("click", () => {
    mSay("Lovely, enjoy! 🧡");
    mButtons("none");
  });
  mConfirm?.addEventListener("click", async () => {
    if (!missingFor || !(mConfirm instanceof HTMLButtonElement)) return;
    mConfirm.disabled = true;
    mSay("Sending…");
    try {
      const body = new FormData();
      body.set("order", missingFor.order);
      body.set("email", missingFor.email);
      const res = await fetch(track.dataset.missingEndpoint ?? "", { method: "POST", body });
      const r = await res.json();
      mSay(
        r.ok
          ? `Thank you for telling me. I'm checking with ${whoDelivers()} and I'll be in touch soon.`
          : (r.error ?? "I couldn't find that order with that email."),
      );
    } catch {
      mSay("That didn't go through. Please message me on WhatsApp and I'll look into it.");
    } finally {
      mButtons("none");
      mConfirm.disabled = false;
    }
  });

  // The order confirmation email links here with the order and email filled in (issue 256).
  const params = new URLSearchParams(location.search);
  const pOrder = params.get("order");
  const pEmail = params.get("email");
  useLinkKey(trackForm);
  if (pOrder && (pEmail || linkKey)) {
    const o = trackForm.querySelector('[name="order"]');
    const m = trackForm.querySelector('[name="email"]');
    if (o instanceof HTMLInputElement) o.value = pOrder;
    if (m instanceof HTMLInputElement && pEmail) m.value = pEmail;
    trackForm.requestSubmit();
  }
}

// ---- Report a problem (issue 43) ----------------------------------------------------------------
// Step 1 finds the order (number and email) through the app proxy and lists its items; step 2
// sends the items, photos and choice straight to CCK Ops with the upload pass step 1 gave.

const report = document.querySelector("[data-cck-report]");
const findForm = report?.querySelector("[data-cck-report-find]");
const reportForm = report?.querySelector("[data-cck-report-form]");
if (
  report instanceof HTMLElement &&
  findForm instanceof HTMLFormElement &&
  reportForm instanceof HTMLFormElement
) {
  /** @type {{ order: string, email: string, token: string, uploadUrl: string, refundOnly: boolean } | null} */
  let found = null;
  const fallback = report.querySelector("[data-cck-report-fallback]");
  const blocked = report.querySelector("[data-cck-report-blocked]");
  const show = (/** @type {Element | null} */ el, /** @type {boolean} */ on) => {
    if (el instanceof HTMLElement) el.hidden = !on;
  };

  findForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(findForm);
    const order = orderNumber(String(data.get("order") ?? ""));
    // Issue 288: the email typed, or the link's key.
    const email = String(data.get("email") ?? "").trim() || linkKey || "";
    /** @type {Record<string, string>} */
    const errors = {};
    if (!order) errors.order = "Your order number is in your confirmation email, like #1042.";
    if (!linkKey && !isEmail(email)) errors.email = "The email you ordered with, please.";
    showErrors(findForm, errors);
    if (Object.keys(errors).length) return;
    show(blocked, false);
    show(reportForm, false);
    try {
      const url = `${report.dataset.endpoint}?order=${encodeURIComponent(order ?? "")}&email=${encodeURIComponent(email)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const o = await res.json();
      if (o.found === false) {
        if (linkKey) showErrors(findForm, { email: dropLinkKey(findForm) });
        else
          showErrors(findForm, {
            order:
              "I couldn't find that order with that email. Check both against your confirmation email.",
          });
        return;
      }
      if (o.blocked) {
        const text = report.querySelector("[data-cck-report-blocked-text]");
        if (text) text.textContent = o.blocked;
        show(blocked, true);
        return;
      }
      found = {
        order: o.orderName,
        email,
        token: o.token,
        uploadUrl: o.uploadUrl,
        refundOnly: o.refundOnly,
      };
      renderItems(o.orderName, o.items, o.refundOnly, o.region ?? null);
      show(reportForm, true);
      show(fallback, false);
      reportForm.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      show(fallback, true);
    }
  });

  /** One row per item: tick it, then how many of them are affected. */
  function renderItems(
    /** @type {string} */ orderName,
    /** @type {{ id: string, title: string, variant: string, quantity: number, price: number, kind: string, shipsAlone: boolean }[]} */ items,
    /** @type {boolean} */ refundOnly,
    /** @type {"ncr" | "national" | null} */ region,
  ) {
    if (!(reportForm instanceof HTMLFormElement)) return;
    const head = reportForm.querySelector("[data-cck-report-order-name]");
    if (head) head.textContent = `Order ${orderName}`;
    const list = reportForm.querySelector("[data-cck-report-items]");
    if (!list) return;
    list.innerHTML = "";
    items.forEach((it, i) => {
      const li = document.createElement("li");
      li.className = "cck-orders__item";
      const label = document.createElement("label");
      label.className = "cck-orders__check";
      const box = Object.assign(document.createElement("input"), {
        type: "checkbox",
        name: "items",
        value: it.id,
      });
      const name = document.createElement("span");
      name.textContent = `${it.title}${it.variant ? ` (${it.variant})` : ""}`;
      label.append(box, name);
      const qty = Object.assign(document.createElement("select"), {
        name: `qty-${i}`,
        className: "cck-orders__qty",
        disabled: true,
      });
      qty.setAttribute("aria-label", `How many ${it.title}`);
      qty.dataset.item = it.id;
      for (let n = 1; n <= it.quantity; n++)
        qty.append(new Option(`${n} of ${it.quantity}`, String(n)));
      qty.value = String(it.quantity);
      box.addEventListener("change", () => {
        qty.disabled = !box.checked;
        sizeCheck();
      });
      qty.addEventListener("change", sizeCheck);
      li.append(label, qty);
      list.append(li);
    });
    // A refund only: no replacement, and no credit either.
    const credit = reportForm.querySelector("[data-cck-credit] input");
    if (credit instanceof HTMLInputElement) {
      credit.disabled = refundOnly;
      if (refundOnly) credit.checked = false;
    }
    show(reportForm.querySelector("[data-cck-refund-only]"), refundOnly);
    sizeCheck();

    /** Issue 136: a replacement only when the ticked items would make an order on their own. */
    function sizeCheck() {
      if (!(reportForm instanceof HTMLFormElement)) return;
      const picked = items
        .map((it) => {
          const box = reportForm.querySelector(`input[name="items"][value="${CSS.escape(it.id)}"]`);
          const sel = reportForm.querySelector(`select[data-item="${CSS.escape(it.id)}"]`);
          return box instanceof HTMLInputElement && box.checked
            ? { id: it.id, quantity: Number(sel instanceof HTMLSelectElement ? sel.value : 1) }
            : null;
        })
        .filter((p) => p !== null);
      const rule = replacementRule({ region, lines: items }, picked);
      const tooSmall = picked.length > 0 && !rule.allowed;
      const replacement = reportForm.querySelector("[data-cck-replacement] input");
      if (replacement instanceof HTMLInputElement) {
        replacement.disabled = refundOnly || tooSmall;
        if (replacement.disabled) replacement.checked = false;
      }
      const hint = reportForm.querySelector("[data-cck-too-small]");
      if (hint) hint.textContent = !refundOnly && tooSmall ? (rule.why ?? "") : "";
      show(hint, !refundOnly && tooSmall);
    }
  }

  // List the chosen photos, so the customer sees what will be sent (and that it's 5 at most).
  const input = reportForm.querySelector("[data-cck-photos]");
  const list = reportForm.querySelector("[data-cck-photo-list]");
  input?.addEventListener("change", () => {
    if (!(input instanceof HTMLInputElement) || !(list instanceof HTMLElement)) return;
    list.innerHTML = "";
    for (const f of input.files ?? []) {
      list.append(Object.assign(document.createElement("li"), { textContent: f.name }));
    }
    const n = input.files?.length ?? 0;
    const p = reportForm.querySelector('[data-cck-error="photos"]');
    if (p)
      p.textContent =
        n > MAX_PHOTOS ? `Up to ${MAX_PHOTOS} photos, please: pick the clearest ones.` : "";
  });

  /**
   * Phone photos can be 4–8 MB each; five over mobile data is slow and can fail (issue 192). Each
   * is redrawn at most 1600 px on its long side, as a JPEG, before it's sent: plenty to see a
   * puffed-up or leaking pouch. A photo the browser can't open (some HEIC) goes as it is.
   * @param {File} file
   * @returns {Promise<File>}
   */
  async function shrink(file) {
    const MAX = 1600;
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
      if (scale === 1 && file.size < 600_000) return file;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!(blob instanceof Blob) || blob.size >= file.size) return file;
      return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch {
      return file;
    }
  }

  // Issue 284: for something missing or wrong, photos help but aren't needed.
  reportForm.addEventListener("change", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.name !== "kind") return;
    const missing = t.value === "missing";
    const optional = reportForm.querySelector("[data-cck-photos-optional]");
    const hint = reportForm.querySelector("[data-cck-photos-hint]");
    if (optional instanceof HTMLElement) optional.hidden = !missing;
    if (hint)
      hint.textContent = missing
        ? "A photo of what arrived (the box and the pouches) helps me check with the kitchen."
        : "A photo of each affected pouch, especially if it's puffed up, leaking or smells off.";
  });

  reportForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!found) return;
    const data = new FormData(reportForm);
    const photos = input instanceof HTMLInputElement ? [...(input.files ?? [])] : [];
    const ids = data.getAll("items").map(String);
    const claim = {
      order: found.order,
      email: found.email,
      items: ids,
      photos: photos.map((f) => ({ name: f.name, type: f.type, size: f.size })),
      choice: String(data.get("choice") ?? ""),
      kind: String(data.get("kind") ?? "damaged"),
      // Issue 335: found through the link's key, so no email to check (the upload pass proves it).
      byKey: !found.email.includes("@"),
    };
    const errors = claimErrors(claim);
    showErrors(reportForm, errors);
    if (Object.keys(errors).length) return;

    const body = new FormData();
    body.set("token", found.token);
    body.set("order", found.order);
    body.set("kind", claim.kind);
    body.set(
      "items",
      JSON.stringify(
        ids.map((id) => {
          const sel = reportForm.querySelector(`select[data-item="${CSS.escape(id)}"]`);
          return { id, quantity: Number(sel instanceof HTMLSelectElement ? sel.value : 1) };
        }),
      ),
    );
    body.set("choice", claim.choice);
    body.set("note", String(data.get("note") ?? ""));
    const send = reportForm.querySelector('[data-cck-error="send"]');
    const button = reportForm.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;
    for (const [i, p] of photos.entries()) {
      if (send) send.textContent = `Preparing photo ${i + 1} of ${photos.length}…`;
      body.append("photos", await shrink(p));
    }
    if (send) send.textContent = "Sending…";
    try {
      const res = await fetch(found.uploadUrl, { method: "POST", body });
      const r = await res.json().catch(() => ({}));
      if (res.status === 422 || r.ok === false) {
        if (send) send.textContent = r.error ?? "Something's not right with the report.";
        if (button instanceof HTMLButtonElement) button.disabled = false;
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      reportForm.hidden = true;
      findForm.hidden = true;
      show(report.querySelector("[data-cck-report-done]"), true);
    } catch {
      if (send) send.textContent = "";
      if (button instanceof HTMLButtonElement) button.disabled = false;
      show(fallback, true);
    }
  });

  // The customer account's "Report a problem" (issue 275) links here with the order filled in.
  const given = new URLSearchParams(location.search);
  const gOrder = given.get("order");
  const gEmail = given.get("email");
  useLinkKey(findForm);
  if (gOrder && (gEmail || linkKey)) {
    const o = findForm.querySelector('[name="order"]');
    const m = findForm.querySelector('[name="email"]');
    if (o instanceof HTMLInputElement) o.value = gOrder;
    if (m instanceof HTMLInputElement && gEmail) m.value = gEmail;
    findForm.requestSubmit();
  }
}
